import {
	BlockTypeSelect,
	BoldItalicUnderlineToggles,
	CreateLink,
	headingsPlugin,
	imagePlugin,
	InsertImage,
	linkDialogPlugin,
	linkPlugin,
	listsPlugin,
	ListsToggle,
	markdownShortcutPlugin,
	MDXEditor,
	MDXEditorMethods,
	quotePlugin,
	Separator,
	thematicBreakPlugin,
	toolbarPlugin,
	UndoRedo,
} from '@mdxeditor/editor';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { Moon, Sun } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { ShowError } from '../../../../errors/components/ShowError';
import { UsersListLoading } from '../../../user-management/users-list/components/loading/UsersListLoading';
import { PostModalState } from '../../posts-table/model';
import {
	useCreatePost,
	useDeletePost,
	useUpdatePost,
	useUploadPostImage,
} from '../api/posts.mutations';
import { useGetPostTags } from '../../../feed-tags/tags-table/api/tags.queries';
import { PostPreview } from './PostPreview';

import '@mdxeditor/editor/style.css';
import './mdx-editor-overrides.css';
import './article-content.css';

const LANGUAGES = ['ru', 'en', 'hi', 'id', 'zh'] as const;
type Lang = typeof LANGUAGES[number];

const EMPTY_LANGS: Record<Lang, string> = {
	ru: '',
	en: '',
	hi: '',
	id: '',
	zh: '',
};

// Обязательны только ru и en; остальные языки — опциональны (валидируются
// лишь если заполнены).
const REQUIRED_LANGS: ReadonlySet<Lang> = new Set<Lang>(['ru', 'en']);

const buildLangShape = (min: number, max: number | undefined, fieldLabel: string) =>
	Yup.object().shape(
		LANGUAGES.reduce((acc, lang) => {
			const base = max
				? Yup.string().max(max, `Максимум ${max} символов`)
				: Yup.string();
			if (REQUIRED_LANGS.has(lang)) {
				acc[lang] = base
					.min(min, `Минимум ${min} символа`)
					.required(`Введите ${fieldLabel} (${lang.toUpperCase()})`);
			} else {
				// Пустая строка -> undefined, чтобы .min не срабатывал на пустом
				// необязательном языке.
				acc[lang] = base
					.transform(value => (value === '' ? undefined : value))
					.min(min, `Минимум ${min} символа`)
					.notRequired();
			}
			return acc;
		}, {} as Record<Lang, Yup.StringSchema<any>>)
	);

const PostSchema = Yup.object().shape({
	name: buildLangShape(3, 50, 'название'),
	text: buildLangShape(3, undefined, 'текст'),
	tag_uuid: Yup.string().required('Выберите тег'),
});

interface Props {
	openModal: PostModalState;
	handleModalClose: () => void;
	setShowErrorToast: (state: boolean) => void;
}

const PostModalForm: FC<Props> = ({
	openModal,
	handleModalClose,
	setShowErrorToast,
}) => {
	const [error, setError] = useState();
	const [activeLang, setActiveLang] = useState<Lang>('ru');
	const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
	const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
	const [previewMarkdown, setPreviewMarkdown] = useState('');
	const mdxEditorRef = useRef<MDXEditorMethods>(null);

	const {
		mutate: createPost,
		isLoading: isPostCreateLoading,
		error: PostCreateError,
	} = useCreatePost();

	const {
		mutate: uploadImage,
		mutateAsync: uploadImageAsync,
		isLoading: isUploadImageLoading,
		error: UploadImageError,
	} = useUploadPostImage();

	// Загрузчик для инлайн-картинок редактора: грузит файл тем же
	// /admin/posts/upload и возвращает корне-относительный путь для markdown
	// (![](/media/posts/...)). Слэш — как у превью обложки и у публичного сайта.
	const imageUploadHandler = async (file: File): Promise<string> => {
		const data = await uploadImageAsync(file);
		return `/${data.image_path}`;
	};

	const {
		mutate: editPost,
		isLoading: isPostEditLoading,
		error: PostEditError,
	} = useUpdatePost();

	const {
		mutate: deletePost,
		isLoading: isPostDeleteLoading,
		error: PostDeleteError,
	} = useDeletePost();

	const isLoading =
		isPostCreateLoading ||
		isUploadImageLoading ||
		isPostEditLoading ||
		isPostDeleteLoading;

	const { data: tags = [] } = useGetPostTags();

	const isDelete = openModal.type === 'delete' && openModal.data;

	const formik = useFormik({
		initialValues: {
			name: { ...EMPTY_LANGS },
			text: { ...EMPTY_LANGS },
			tag_uuid: '',
			image_path: '',
		},
		validationSchema: !isDelete ? PostSchema : null,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);

			try {
				if (openModal.type === 'create') {
					createPost(values);
					return;
				}
				if (openModal.type === 'edit' && openModal.data) {
					editPost({ uuid: openModal.data.uuid, request: values });
					return;
				}
				if (openModal.type === 'delete' && openModal.data) {
					deletePost(openModal.data.uuid);
					return;
				}
			} catch (ex) {
				setShowErrorToast(true);
				console.error(ex);
			} finally {
				setSubmitting(true);
				handleModalClose();
			}
		},
	});

	const handleLangChange = (lang: Lang) => {
		formik.setFieldValue(`text.${activeLang}`, mdxEditorRef.current?.getMarkdown() || '');
		setActiveLang(lang);
		if (viewMode === 'preview') {
			setPreviewMarkdown(formik.values.text[lang] || '');
		} else {
			setTimeout(() => {
				mdxEditorRef.current?.setMarkdown(formik.values.text[lang] || '');
			}, 0);
		}
	};

	// В превью показываем самую свежую версию текста активного языка: снимаем
	// её из редактора и параллельно синхронизируем во formik.
	const showPreview = () => {
		const markdown = mdxEditorRef.current?.getMarkdown() ?? formik.values.text[activeLang] ?? '';
		formik.setFieldValue(`text.${activeLang}`, markdown);
		setPreviewMarkdown(markdown);
		setViewMode('preview');
	};

	// Возврат в редактор: восстанавливаем markdown активного языка (редактор
	// был скрыт, но смонтирован).
	const showEditor = () => {
		setViewMode('edit');
		setTimeout(() => {
			mdxEditorRef.current?.setMarkdown(formik.values.text[activeLang] || '');
		}, 0);
	};

	useEffect(() => {
		if (openModal.type === 'edit' && openModal.data) {
			const nameData = openModal.data.name || { ...EMPTY_LANGS };
			const textData = openModal.data.text || { ...EMPTY_LANGS };

			formik.setValues({
				name: { ...EMPTY_LANGS, ...nameData },
				text: { ...EMPTY_LANGS, ...textData },
				tag_uuid: openModal.data.tag_uuid || '',
				image_path: openModal.data.image_path || '',
			});

			setActiveLang('ru');
			setTimeout(() => {
				mdxEditorRef.current?.setMarkdown(textData.ru || '');
			}, 0);
		} else if (openModal.type === 'create') {
			setActiveLang('ru');
			mdxEditorRef.current?.setMarkdown('');
		}
	}, [openModal]);

	const nameError = (formik.errors.name as any)?.[activeLang];
	const nameTouched = (formik.touched.name as any)?.[activeLang];
	const textError = (formik.errors.text as any)?.[activeLang];
	const textTouched = (formik.touched.text as any)?.[activeLang];

	const langHasError = (lang: Lang) => {
		const nameErr =
			!!(formik.touched.name as any)?.[lang] &&
			!!(formik.errors.name as any)?.[lang];
		const textErr =
			!!(formik.touched.text as any)?.[lang] &&
			!!(formik.errors.text as any)?.[lang];
		return nameErr || textErr;
	};

	const deleteName = openModal.data?.name
		? (openModal.data.name.ru || Object.values(openModal.data.name)[0] || '')
		: '';

	return (
		<>
			<form
				id='kt_modal_Post_form'
				className='form'
				onSubmit={formik.handleSubmit}
				noValidate
			>
				{/* begin::Scroll */}
				<div
					className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
					id='kt_modal_add_user_scroll'
					data-kt-scroll='true'
					data-kt-scroll-activate='{default: false, lg: true}'
					data-kt-scroll-max-height='auto'
					data-kt-scroll-dependencies='#kt_modal_add_user_header'
					data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
					data-kt-scroll-offset='300px'
				>
					{openModal.type !== 'delete' && (
						<div>
							{/* begin::Image upload */}
							<div className='fv-row mb-7'>
								<label className='fw-bold fs-sm-6 fs-7 mb-2'>Изображение</label>

								<input
									type='file'
									accept='image/*'
									onChange={async e => {
										if (!e.target.files?.[0]) return;

										const file = e.target.files[0];
										try {
											uploadImage(file, {
												onSuccess: data => {
													formik.setFieldValue(
														'image_path',
														data.image_path || ''
													);
												},
												onError: err => {
													setError(err);
													setShowErrorToast(true);
												},
											});
										} catch (ex) {
											console.error(ex);
										}
									}}
									className='form-control form-control-solid'
									disabled={formik.isSubmitting || isUploadImageLoading}
								/>

								{formik.values.image_path && (
									<div className='mt-2'>
										<img
											src={`/${formik.values.image_path}`}
											alt='Preview'
											style={{ maxWidth: '150px', maxHeight: '150px' }}
										/>
									</div>
								)}
							</div>
							{/* end::Image upload */}

							{/* begin::Language tabs */}
							<div className='mb-5'>
								<ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0'>
									{LANGUAGES.map(lang => {
										const hasError = langHasError(lang);
										return (
											<li key={lang} className='nav-item'>
												<button
													type='button'
													className={clsx('nav-link text-uppercase fw-bold', {
														active: activeLang === lang,
														'text-danger': hasError,
													})}
													onClick={() => handleLangChange(lang)}
												>
													{lang}
													{REQUIRED_LANGS.has(lang) && (
														<span className='ms-1 text-muted' aria-hidden='true'>*</span>
													)}
													{hasError && (
														<span className='ms-1 text-danger' aria-hidden='true'>•</span>
													)}
												</button>
											</li>
										);
									})}
								</ul>
							</div>
							{/* end::Language tabs */}

							{/* begin::Name input */}
							<div className='fv-row mb-7'>
								<label
									className={clsx('fw-bold fs-sm-6 fs-7 mb-2', {
										required: REQUIRED_LANGS.has(activeLang),
									})}
								>
									Название
								</label>

								<input
									placeholder='Название поста'
									{...formik.getFieldProps(`name.${activeLang}`)}
									type='text'
									className={clsx(
										'form-control form-control-solid mb-3 mb-lg-0',
										{
											'is-invalid': nameTouched && nameError,
										},
										{
											'is-valid': nameTouched && !nameError,
										}
									)}
									autoComplete='off'
									disabled={formik.isSubmitting}
								/>

								{nameTouched && nameError && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{nameError}</span>
										</div>
									</div>
								)}
							</div>
							{/* end::Name input */}

							{/* begin::Tag select */}
							<div className='fv-row mb-7'>
								<label className='required fw-bold fs-sm-6 fs-7 mb-2'>
									Тег
								</label>

								<select
									{...formik.getFieldProps('tag_uuid')}
									name='tag_uuid'
									className={clsx(
										'form-select form-select-solid mb-3 mb-lg-0',
										{
											'is-invalid':
												formik.touched.tag_uuid && formik.errors.tag_uuid,
										},
										{
											'is-valid':
												formik.touched.tag_uuid && !formik.errors.tag_uuid,
										}
									)}
									disabled={formik.isSubmitting}
								>
									<option value=''>— Выберите тег —</option>
									{tags.map(tag => (
										<option key={tag.uuid} value={tag.uuid}>
											{tag.name?.ru || Object.values(tag.name || {})[0] || ''}
										</option>
									))}
								</select>

								{formik.touched.tag_uuid && formik.errors.tag_uuid && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.tag_uuid}</span>
										</div>
									</div>
								)}
							</div>
							{/* end::Tag select */}

							{/* begin::Text editor */}
							<div className='fv-row mb-7'>
								<div className='d-flex align-items-center justify-content-between mb-2'>
									<label
										className={clsx('fw-bold fs-sm-6 fs-7 mb-0', {
											required: REQUIRED_LANGS.has(activeLang),
										})}
									>
										Текст поста
									</label>

									<div className='d-flex align-items-center gap-2'>
										{viewMode === 'preview' && (
											<div
												className='btn-group btn-group-sm'
												role='group'
												aria-label='Тема превью'
											>
												<button
													type='button'
													className={clsx(
														'btn btn-sm btn-icon',
														previewTheme === 'light' ? 'btn-primary' : 'btn-light'
													)}
													onClick={() => setPreviewTheme('light')}
													title='Светлая тема'
												>
													<Sun size={16} />
												</button>
												<button
													type='button'
													className={clsx(
														'btn btn-sm btn-icon',
														previewTheme === 'dark' ? 'btn-primary' : 'btn-light'
													)}
													onClick={() => setPreviewTheme('dark')}
													title='Тёмная тема'
												>
													<Moon size={16} />
												</button>
											</div>
										)}

										<div
											className='btn-group btn-group-sm'
											role='group'
											aria-label='Режим'
										>
											<button
												type='button'
												className={clsx(
													'btn btn-sm',
													viewMode === 'edit' ? 'btn-primary' : 'btn-light'
												)}
												onClick={showEditor}
											>
												Редактор
											</button>
											<button
												type='button'
												className={clsx(
													'btn btn-sm',
													viewMode === 'preview' ? 'btn-primary' : 'btn-light'
												)}
												onClick={showPreview}
											>
												Превью
											</button>
										</div>
									</div>
								</div>

								<div
									className={clsx(
										'border rounded min-h-300px',
										{
											'd-none': viewMode === 'preview',
										},
										{
											'border-danger': textTouched && textError,
										},
										{
											'border-primary': textTouched && !textError,
										}
									)}
								>
									<MDXEditor
										ref={mdxEditorRef}
										markdown={formik.values.text[activeLang]}
										onChange={markdown =>
											formik.setFieldValue(`text.${activeLang}`, markdown)
										}
										onBlur={() => formik.setFieldTouched(`text.${activeLang}`, true)}
										contentEditableClassName='article-content'
										plugins={[
											headingsPlugin(),
											listsPlugin(),
											quotePlugin(),
											thematicBreakPlugin(),
											linkPlugin(),
											linkDialogPlugin(),
											imagePlugin({ imageUploadHandler }),
											markdownShortcutPlugin(),
											toolbarPlugin({
												toolbarContents: () => (
													<>
														<UndoRedo />
														<Separator />
														<BlockTypeSelect />
														<Separator />
														<BoldItalicUnderlineToggles />
														<Separator />
														<ListsToggle />
														<Separator />
														<CreateLink />
														<InsertImage />
													</>
												),
											}),
										]}
									/>
								</div>

								{viewMode === 'preview' && (
									<div className='border rounded overflow-hidden'>
										<PostPreview
											markdown={previewMarkdown}
											theme={previewTheme}
										/>
									</div>
								)}

								{textTouched && textError && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert' className='text-danger'>
												{textError}
											</span>
										</div>
									</div>
								)}
							</div>
							{/* end::Text editor */}
						</div>
					)}

					{openModal.type === 'delete' && openModal.data && (
						<div className='fs-6 text-gray-700'>
							Вы собираетесь удалить <strong>{deleteName}</strong>.
							Пожалуйста, подтвердите свое действие.
						</div>
					)}

					{error && <ShowError error={error} />}
				</div>
				{/* end::Scroll */}

				{/* begin::Actions */}
				<div className='text-center mt-5 '>
					<button
						type='reset'
						className='btn btn-light btn-sm btn-sm-md me-5 min-w-125px'
						onClick={handleModalClose}
						data-kt-users-modal-action='cancel'
						disabled={formik.isSubmitting}
					>
						Отмена
					</button>

					<button
						type='submit'
						className='btn btn-primary  btn-sm btn-sm-md min-w-125px'
						data-kt-users-modal-action='submit'
						disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
					>
						<span className='indicator-label'>
							{openModal.type === 'create'
								? 'Создать'
								: openModal.type === 'edit'
								? 'Изменить'
								: 'Удалить'}
						</span>
						{formik.isSubmitting && (
							<span className='indicator-progress'>
								Пожалуйста подождите...{' '}
								<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
							</span>
						)}
					</button>
				</div>
				{/* end::Actions */}
			</form>
			{formik.isSubmitting && <UsersListLoading />}
		</>
	);
};

export { PostModalForm };
